import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

// Styles pour la page de connexion
const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--spacing-lg);
  background-color: var(--color-light-gray);
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 400px;
  padding: var(--spacing-lg);
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const LoginTitle = styled.h1`
  margin-bottom: var(--spacing-lg);
  text-align: center;
  font-weight: var(--font-weight-bold);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: var(--font-weight-medium);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid var(--color-medium-gray);
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-text);
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: var(--color-text);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #333;
  }
  
  &:disabled {
    background-color: var(--color-medium-gray);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const HomeLink = styled.a`
  display: block;
  text-align: center;
  margin-top: var(--spacing-md);
  color: var(--color-text);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Assurer que useLocation est utilisé
  const { signInWithPassword, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Si l'authentification n'est plus en chargement et que l'utilisateur est connecté,
    // rediriger vers la page admin.
    // Cela gère la redirection après une connexion réussie et si un utilisateur
    // déjà connecté accède à /login.
    if (!authLoading && user) {
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, location]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
        // Tentative de connexion avec Supabase
    signInWithPassword({ email, password })
      .then(({ error: authError }) => {
        if (authError) {
          setError(authError.message || 'Erreur d\'authentification. Vérifiez vos identifiants.');
          setIsLoading(false);
        } else {
          // La redirection est maintenant gérée par le useEffect ci-dessus.
          // On s'assure juste que l'état de chargement du formulaire est bien remis à false.
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error("Login Page Error:", err);
        setError('Une erreur inattendue est survenue.');
        setIsLoading(false);
      });
  };
  
  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <LoginTitle>Connexion Admin</LoginTitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <FormGroup>
          <FormLabel htmlFor="email">Email</FormLabel>
          <FormInput
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="password">Mot de passe</FormLabel>
          <FormInput
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </FormGroup>
        
        <LoginButton type="submit" disabled={isLoading}>
          {isLoading ? 'Connexion...' : 'Se connecter'}
        </LoginButton>
        
        <HomeLink href="/">Retour à l'accueil</HomeLink>
      </LoginForm>
    </LoginContainer>
  );
};

export default LoginPage;
